'use client';

import { logger } from '@dealerportal/core';
import React from 'react';

import type { Filter, SRPFilterAction, SRPState } from '@dealerportal/common';
import {
  ClientContextProvider,
  FavouriteProductsContextProvider,
} from '@dealerportal/react-helpers';
import { EncodeFiltersToSearchParams } from '@dealerportal/utilities';
import classNames from 'classnames';
import { ToastContainer } from 'react-toastify';
import {
  CatalogContextProvider,
  type ICatalogSettings,
} from './Contexts/CatalogContext';
import { useCiCItemLookup } from './Hooks/useCiCItemLookup';
import { FilterPanel } from './Modules/Filters/FilterPanel';
import { SRPPaginator } from './Modules/Paginations/SRPPaginator';
import { ProductDetails } from './Modules/ProductDetails/ProductDetails';
import { ProductResults } from './Modules/ProductResults/ProductResults';

const universalRetailerId = '314aa161-867a-4902-b780-35c62319b418';
const clientId = 2482;

function buildSRPUrl(filters: Filter[], sort?: string): string {
  const clientQuery = new URL(
    window.location.pathname,
    'https://any-base-path-will-do-here.com'
  );
  const newSearchParams = EncodeFiltersToSearchParams(
    clientQuery.searchParams,
    filters
  );
  if (sort) {
    newSearchParams.set('sort', sort);
  }
  let search = `?${newSearchParams.toString()}`;
  if (search.length === 1) search = '';
  return search;
}

/* eslint-disable-next-line */
// biome-ignore lint/suspicious/noEmptyInterface: <explanation>
export interface CatalogUiProps {}

/**
 * Apply filter actions to the SRPstate
 * @param state the SRPState to modify filters on
 * @param action the action to perform on the filters
 * @returns a modified SRP state
 */
export function ApplyFilterAction(
  state: SRPState,
  action: SRPFilterAction & { filter: string }
): SRPState {
  const newFilters: Filter[] = JSON.parse(
    JSON.stringify(state.filters)
  ) as Filter[];
  const filter = newFilters.find(({ name }) => name === action.filter);
  if (filter == null) return state;
  if (filter.enabledValues == null) filter.enabledValues = [];
  switch (action.type) {
    case 'SET_ALL_TO': {
      filter.enabledValues = [action.value];
      break;
    }
    case 'UPDATE': {
      filter.enabledValues.push(action.value);
      break;
    }
    case 'REMOVE': {
      const indexOf = filter.enabledValues.findIndex(
        (value) =>
          value === action.value ||
          `${value}`.toLowerCase() === `${action.value}`.toLowerCase()
      );
      if (indexOf === -1) {
        logger.warn(
          `Couldn't find filter (${action.filter}: ${action.value}) to remove`
        );
      }
      filter.enabledValues = [
        ...filter.enabledValues.slice(0, indexOf),
        ...filter.enabledValues.slice(indexOf + 1, filter.enabledValues.length),
      ];
      break;
    }
  }
  // Deduplicate enabled values
  filter.enabledValues = Array.from(new Set(filter.enabledValues));
  const ret = { ...state, filters: newFilters, needsHydration: true };
  return ret;
}

type SRPProps = ICatalogSettings;

export function SRP(props: SRPProps) {
  const searchParams = new URLSearchParams(window.location.search);
  return (
    <div>
      <FavouriteProductsContextProvider clientId={clientId}>
        <CatalogContextProvider
          initialHasPricing={false}
          initialTags={[]}
          initialState={{
            loading: true,
            needsHydration: false,
            filters: [],
            sort: '',
          }}
          reducer={(push) => (state, action) => {
            let doNavigation = false;
            switch (action?.type) {
              case 'RESET':
                state.filters = action.filters;
                state.loading = false;
                break;
              case 'CLEAR_ALL':
                for (const filter of state.filters) {
                  filter.enabledValues = [];
                }
                doNavigation = true;
                break;
              case 'APPLY':
                doNavigation = true;
                break;
              case 'HYDRATE_FILTERS':
                // state.filters = HydrateFilters(
                //   state.filters,
                //   action.hydratedFilters
                // );
                state.needsHydration = false;
                break;
              case 'HYDRATE_FILTERS_FAIL':
                state.needsHydration = false;
                break;
              case 'UPDATE_SORT':
                state.sort = action.value;
                doNavigation = true;
                break;
              default:
                state = ApplyFilterAction(state, action);
                break;
            }
            if (doNavigation) push(buildSRPUrl(state.filters, state.sort));

            state.needsHydration = false;

            return state;
          }}
          useCiC={true}
          settings={{ hideOrderSampleButton: props.hideOrderSampleButton }}
        >
          <div className="srp__container ui-container md:!ui-max-w-none lg:!ui-max-w-screen-xl ui-min-h-[40vh] ui-pt-8 md:ui-pt-12">
            <div className="ui-grid md:ui-grid-cols-3 lg:ui-grid-cols-4 ui-gap-8">
              <FilterPanel isMobileDevice={false} />
              <div
                className={classNames(
                  'srp__col-left ui-min-w-0 md:ui-col-span-2 lg:ui-col-span-3'
                )}
              >
                <ProductResults />

                <div id="pagination" className="srp__pagination ui-mt-6">
                  <SRPPaginator initialPage={0} />
                </div>
              </div>
            </div>
          </div>
          <ToastContainer
            hideProgressBar
            autoClose={1000}
            position="bottom-right"
          />
        </CatalogContextProvider>
      </FavouriteProductsContextProvider>
    </div>
  );
}

type PDPProps = {
  catalogCode: string;
  itemId: string;
} & ICatalogSettings;

export const PDP = (props: PDPProps) => {
  const { products } = useCiCItemLookup([props.itemId]);

  if (products.length === 0) return;

  return (
    <FavouriteProductsContextProvider clientId={0}>
      <ClientContextProvider
        clientId={clientId}
        disableOrderSample={true}
        isMobileDevice={false}
        clientName="Hawkins"
        universalRetailerId={universalRetailerId}
        useCiC={true}
        tokens={[
          {
            TokenTypeId: 3,
            TokenName: 'ff-viz-api-token',
            Replacement: 'g25pe98duhd2pvtz',
          },
        ]}
        recaptcha={{
          Enabled: false,
          SiteKey: '',
        }}
      >
        <CatalogContextProvider
          initialHasPricing={false}
          initialTags={[]}
          initialState={{
            loading: true,
            needsHydration: false,
            filters: [],
            sort: '',
          }}
          platform="MM"
          reducer={() => (state, action) => {
            switch (action.type) {
              case 'RESET':
                state.filters = action.filters;
                state.loading = false;
                break;
            }
            return state;
          }}
          useCiC={true}
          loadProducts={false}
          settings={{ hideOrderSampleButton: props.hideOrderSampleButton }}
        >
          <ProductDetails
            actonForm=""
            isMobileDevice={false}
            marketingContent=""
            topSectionContent=""
            productInfo={products[0]}
          />
          <ToastContainer
            hideProgressBar
            autoClose={1000}
            position="bottom-right"
          />
        </CatalogContextProvider>
      </ClientContextProvider>
    </FavouriteProductsContextProvider>
  );
};
